import { useCallback, useEffect, useMemo, useState } from "react"
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom"
import { Building2, ClipboardList, LayoutDashboard, MessageSquareWarning, Settings2, type LucideIcon } from "lucide-react"
import type { Settings } from "../shared/domain"
import { ErrorBox, Loading } from "@/components/common"
import { MobileTabBar, PillTabs } from "@/components/PillTabs"
import { apiErrorMessage, makeApi, type UserOption } from "@/lib/api"
import { AppDataProvider, type AppData } from "@/lib/app-context"
import { useHostTheme } from "@/lib/host-theme"
import { useAppShell, type AppShell } from "@/lib/shell"
import { cn } from "@/lib/utils"
import { ClientsPage } from "@/pages/ClientsPage"
import { ComplaintDetailPage } from "@/pages/ComplaintDetailPage"
import { ComplaintsPage } from "@/pages/ComplaintsPage"
import { DashboardPage } from "@/pages/DashboardPage"
import { SettingsPage } from "@/pages/SettingsPage"
import { SurveysPage } from "@/pages/SurveysPage"

// Pela ordem de `navigation.children` do manifest. Administração fica fora da
// barra, num botão à direita (como no RH).
type Section = { key: string; path: string; label: string; icon: LucideIcon }

const TABS: Section[] = [
  { key: "painel", path: "/painel", label: "Painel", icon: LayoutDashboard },
  { key: "reclamacoes", path: "/reclamacoes", label: "Reclamações", icon: MessageSquareWarning },
  { key: "inqueritos", path: "/inqueritos", label: "Inquéritos", icon: ClipboardList },
  { key: "clientes", path: "/clientes", label: "Clientes", icon: Building2 },
]
const ADMIN: Section = { key: "definicoes", path: "/definicoes", label: "Administração", icon: Settings2 }
const SECTIONS = [...TABS, ADMIN]

function CenteredMessage({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-lg p-8 text-center">
      <h1 className="text-xl font-semibold">{title}</h1>
      {children ? <div className="mt-2 text-sm text-muted-foreground">{children}</div> : null}
    </div>
  )
}

export function App() {
  const state = useAppShell()
  if (state.kind === "loading") return <Loading label="A inicializar…" />
  if (state.kind === "no-token") {
    return (
      <CenteredMessage title="Satisfação e Reclamações">
        Sem <code className="font-mono">?token=…</code> no URL. Abre a app a partir do Koortex.
      </CenteredMessage>
    )
  }
  if (state.kind === "bad-token") return <CenteredMessage title="Token inválido">{state.error}</CenteredMessage>
  return <Shell shell={state.shell} />
}

function Shell({ shell }: { shell: AppShell }) {
  useHostTheme(shell.context.hostOrigin)
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const api = useMemo(() => makeApi(shell.client), [shell.client])
  const [boot, setBoot] = useState<{ users: UserOption[]; me: string | null; settings: Settings } | null>(null)
  const [bootError, setBootError] = useState<string | null>(null)

  const load = useCallback(() => {
    setBootError(null)
    Promise.all([api.users(), api.settings()])
      .then(([u, s]) => setBoot({ users: u.users, me: u.me, settings: s }))
      .catch((e) => setBootError(apiErrorMessage(e)))
  }, [api])
  useEffect(load, [load])

  // `?token` fica sempre no URL: é dele que a sessão arranca num reload.
  const go = useCallback(
    (to: string) => {
      const [path, query = ""] = to.split("?")
      const params = new URLSearchParams(search)
      for (const k of [...params.keys()]) if (k !== "token") params.delete(k)
      new URLSearchParams(query).forEach((v, k) => params.set(k, v))
      navigate(`${path}?${params}`)
    },
    [navigate, search],
  )

  const data: AppData | null = useMemo(() => {
    if (!boot) return null
    const names = new Map(boot.users.map((u) => [u.id, u.name]))
    return {
      shell,
      api,
      users: boot.users,
      me: boot.me,
      settings: boot.settings,
      setSettings: (settings) => setBoot((b) => (b ? { ...b, settings } : b)),
      go,
      toast: (variant, title, description) => shell.bridge.toast({ variant, title, description }),
      userName: (id) => (id ? (names.get(id) ?? null) : null),
      severityLabel: (key) => (key ? (boot.settings.severities.find((s) => s.key === key)?.label ?? key) : null),
    }
  }, [boot, shell, api, go])

  const active = SECTIONS.find((s) => pathname.startsWith(s.path))?.key ?? "painel"
  const goSection = (key: string) => {
    const s = SECTIONS.find((x) => x.key === key)
    if (s) go(s.path)
  }
  const navTabs = TABS.map(({ key, label, icon }) => ({ key, label, icon }))

  return (
    <div className="mx-auto max-w-6xl px-4 py-5">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">Satisfação e Reclamações</h1>
        <button
          onClick={() => goSection(ADMIN.key)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
            active === ADMIN.key
              ? "border-transparent bg-accent text-accent-foreground shadow-sm"
              : "border-border/60 bg-card/70 text-muted-foreground shadow-sm backdrop-blur hover:bg-secondary/70 hover:text-foreground",
          )}
        >
          <Settings2 className="h-3.5 w-3.5" />
          {ADMIN.label}
        </button>
      </header>

      <div className="mb-5">
        <div className="hidden sm:block">
          <PillTabs tabs={navTabs} value={active} onChange={goSection} />
        </div>
        <MobileTabBar tabs={navTabs} value={active} onChange={goSection} />
      </div>

      {bootError ? <ErrorBox message={bootError} onRetry={load} /> : null}
      {!data ? (
        bootError ? null : <Loading />
      ) : (
        <AppDataProvider value={data}>
          <Routes>
            <Route path="/painel" element={<DashboardPage />} />
            <Route path="/reclamacoes" element={<ComplaintsPage />} />
            <Route path="/reclamacoes/:id" element={<ComplaintDetailPage />} />
            <Route path="/inqueritos" element={<SurveysPage />} />
            <Route path="/clientes" element={<ClientsPage />} />
            <Route path="/definicoes" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to={`/painel${search}`} replace />} />
          </Routes>
        </AppDataProvider>
      )}
    </div>
  )
}
